import React, { useState, useRef } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Lock, Sun, Battery, Zap, Home, Shield, Upload, Camera,
  CheckCircle, AlertTriangle, XCircle, ArrowRight, ArrowLeft,
  Loader2, Info, Phone, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps }) => (
  <div className="flex items-center justify-center gap-2 mb-6">
    {Array.from({ length: totalSteps }, (_, i) => (
      <div
        key={i}
        className={`w-3 h-3 rounded-full transition-all ${
          i < currentStep ? 'bg-[#B5D43B]' : i === currentStep ? 'bg-[#3D5A47]' : 'bg-gray-200'
        }`}
      />
    ))}
  </div>
);

// Option card component
const OptionCard = ({ selected, onClick, icon: Icon, title, description, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
      disabled 
        ? 'opacity-50 cursor-not-allowed border-gray-200' 
        : selected
          ? 'border-[#B5D43B] bg-[#B5D43B]/10'
          : 'border-gray-200 hover:border-[#B5D43B]/50'
    }`}
  >
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
        selected ? 'bg-[#B5D43B]' : 'bg-gray-100'
      }`}>
        <Icon className={`w-6 h-6 ${selected ? 'text-white' : 'text-[#3D5A47]'}`} />
      </div>
      <div className="flex-1">
        <p className="font-semibold text-[#3D5A47]">{title}</p>
        {description && <p className="text-sm text-gray-500">{description}</p>}
      </div>
      {selected && <CheckCircle className="w-6 h-6 text-[#B5D43B]" />}
    </div>
  </button>
);

// Result badge component
const ResultBadge = ({ likelihood }) => {
  const config = {
    unlikely: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Upgrade Unlikely' },
    possible: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertTriangle, label: 'Upgrade Possible' },
    likely: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Upgrade Likely' },
  };
  const { bg, text, icon: Icon, label } = config[likelihood] || config.possible;
  
  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${bg} ${text}`}>
      <Icon className="w-5 h-5" />
      <span className="font-semibold">{label}</span>
    </div>
  );
};

const MSBCheckerPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [accessError, setAccessError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  
  const [formData, setFormData] = useState({
    installationType: '',
    phaseType: '',
    backupType: '',
    imageBase64: '',
    email: '',
  });
  
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  
  const totalSteps = formData.installationType === 'solar_battery' ? 4 : 3;

  // Verify access code
  const verifyAccess = async () => {
    setIsVerifying(true);
    setAccessError('');
    
    try {
      const response = await fetch(`${API_URL}/api/msb/verify-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: accessCode }),
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
      } else {
        setAccessError('Invalid access code. Please try again.');
      }
    } catch (err) {
      setAccessError('Failed to verify access code.');
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle file upload - improved for mobile
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }
    
    console.log('File selected:', file.name, file.type, file.size);
    
    // Validate file type - be more permissive for mobile
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    const isValidType = validTypes.includes(file.type.toLowerCase()) || file.type.startsWith('image/');
    
    if (!isValidType) {
      alert('Please upload an image file (JPG, PNG, WEBP, HEIC)');
      return;
    }
    
    // Validate file size (max 15MB for mobile photos)
    if (file.size > 15 * 1024 * 1024) {
      alert('Image must be smaller than 15MB');
      return;
    }
    
    // Show loading state
    setPreviewImage('loading');
    
    try {
      // Create preview and base64
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const result = event.target?.result;
        if (result && typeof result === 'string') {
          setPreviewImage(result);
          // Extract base64 without data URL prefix
          const base64 = result.split(',')[1];
          if (base64) {
            setFormData(prev => ({ ...prev, imageBase64: base64 }));
            console.log('Image loaded successfully, base64 length:', base64.length);
          }
        }
      };
      
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        alert('Failed to read the image. Please try again.');
        setPreviewImage(null);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Failed to process the image. Please try again.');
      setPreviewImage(null);
    }
  };

  // Handle form navigation
  const goNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Submit analysis
  const submitAnalysis = async () => {
    setIsAnalyzing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/msb/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          installation_type: formData.installationType,
          phase_type: formData.phaseType,
          backup_type: formData.backupType,
          image_base64: formData.imageBase64,
          email: formData.email,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setResult(data);
        setCurrentStep(totalSteps); // Move to results
      } else {
        const error = await response.json();
        alert(error.detail || 'Analysis failed. Please try again.');
      }
    } catch (err) {
      alert('Failed to analyze switchboard. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setCurrentStep(0);
    setResult(null);
    setFormData({
      installationType: '',
      phaseType: '',
      backupType: '',
      imageBase64: '',
      email: '',
    });
    setPreviewImage(null);
  };

  // Render access code screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#3D5A47] to-[#2d4435]">
        <Header />
        <main className="pt-32 pb-16">
          <div className="max-w-md mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#B5D43B]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-[#B5D43B]" />
                </div>
                <h1 className="text-2xl font-bold text-[#3D5A47]">MSB Checker</h1>
                <p className="text-gray-600 mt-2">Enter your access code to continue</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="accessCode" className="text-[#3D5A47]">Access Code</Label>
                  <Input
                    id="accessCode"
                    type="password"
                    placeholder="Enter 5-digit code"
                    value={accessCode}
                    onChange={(e) => {
                      setAccessCode(e.target.value);
                      setAccessError('');
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && verifyAccess()}
                    className="mt-1 text-center text-xl tracking-widest"
                    maxLength={5}
                  />
                </div>
                
                {accessError && (
                  <p className="text-red-500 text-sm text-center">{accessError}</p>
                )}
                
                <Button
                  onClick={verifyAccess}
                  disabled={accessCode.length < 5 || isVerifying}
                  className="w-full bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
                >
                  {isVerifying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Render results screen
  if (result) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-4">
            {/* Result Header */}
            <div className="text-center mb-8">
              <ResultBadge likelihood={result.upgrade_likelihood} />
              <h1 className="text-3xl font-bold text-[#3D5A47] mt-4">Switchboard Analysis Complete</h1>
              <p className="text-gray-600 mt-2">Reference: {result.reference_id}</p>
            </div>

            {/* Space Analysis */}
            <div className="bg-gray-50 rounded-2xl p-6 mb-6">
              <h2 className="font-semibold text-[#3D5A47] mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#B5D43B]" />
                Space Analysis
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-[#3D5A47]">{result.total_slots}</p>
                  <p className="text-sm text-gray-500">Total Slots</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-gray-500">{result.used_slots}</p>
                  <p className="text-sm text-gray-500">Used Slots</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-[#B5D43B]">{result.available_slots}</p>
                  <p className="text-sm text-gray-500">Available</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-orange-500">{result.required_slots}</p>
                  <p className="text-sm text-gray-500">Required</p>
                </div>
              </div>
              <p className="mt-4 text-gray-600 bg-white rounded-lg p-3">
                <Info className="w-4 h-4 inline mr-2 text-[#B5D43B]" />
                {result.upgrade_reason}
              </p>
            </div>

            {/* Panel Condition */}
            <div className="bg-gray-50 rounded-2xl p-6 mb-6">
              <h2 className="font-semibold text-[#3D5A47] mb-3">Panel Condition</h2>
              <p className="text-lg capitalize text-[#3D5A47]">{result.panel_condition}</p>
              
              {result.existing_components?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-gray-500 mb-2">Identified Components:</p>
                  <div className="flex flex-wrap gap-2">
                    {result.existing_components.map((comp, i) => (
                      <span key={i} className="px-3 py-1 bg-white rounded-full text-sm text-[#3D5A47]">
                        {comp}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Findings */}
            {result.findings?.length > 0 && (
              <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                <h2 className="font-semibold text-[#3D5A47] mb-3">Findings</h2>
                <ul className="space-y-2">
                  {result.findings.map((finding, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-600">
                      <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                      {finding}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations */}
            {result.recommendations?.length > 0 && (
              <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                <h2 className="font-semibold text-[#3D5A47] mb-3">Recommendations</h2>
                <ul className="space-y-2">
                  {result.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-600">
                      <ArrowRight className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTA */}
            <div className="bg-[#3D5A47] rounded-2xl p-8 text-center text-white">
              <h2 className="text-xl font-bold mb-2">Ready to proceed?</h2>
              <p className="text-white/80 mb-6">
                Get a detailed quote for your solar{formData.installationType === 'solar_battery' ? ' and battery' : ''} installation
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="/get-estimate"
                  className="inline-flex items-center justify-center gap-2 bg-[#B5D43B] text-[#3D5A47] px-6 py-3 rounded-xl font-semibold hover:bg-[#a3c235] transition-colors"
                >
                  Get Free Estimate
                </a>
                <a
                  href="tel:0478453548"
                  className="inline-flex items-center justify-center gap-2 bg-white/20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/30 transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  Call Us
                </a>
              </div>
            </div>

            {/* Email Results to Customer */}
            <div className="bg-gray-50 rounded-2xl p-6 mt-6">
              <h2 className="font-semibold text-[#3D5A47] mb-3 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#B5D43B]" />
                Get a copy of this report
              </h2>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="pl-10"
                  />
                </div>
                <Button
                  onClick={async () => {
                    if (!formData.email) return;
                    try {
                      await fetch(`${API_URL}/api/msb/send-report`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          reference_id: result.reference_id,
                          email: formData.email
                        }),
                      });
                      alert('Report sent to your email!');
                    } catch (err) {
                      alert('Failed to send email. Please try again.');
                    }
                  }}
                  disabled={!formData.email}
                  className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
                >
                  Send
                </Button>
              </div>
            </div>

            {/* Start Over */}
            <div className="text-center mt-6">
              <button
                onClick={resetForm}
                className="text-gray-500 hover:text-[#B5D43B]"
              >
                ← Analyze Another Switchboard
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Render form steps
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <h2 className="text-xl font-bold text-[#3D5A47] mb-2 text-center">Installation Type</h2>
            <p className="text-gray-600 text-center mb-6">What type of installation are you planning?</p>
            <div className="space-y-3">
              <OptionCard
                selected={formData.installationType === 'solar_only'}
                onClick={() => setFormData(prev => ({ ...prev, installationType: 'solar_only', backupType: '' }))}
                icon={Sun}
                title="Solar Only"
                description="Solar panel system without battery storage"
              />
              <OptionCard
                selected={formData.installationType === 'solar_battery'}
                onClick={() => setFormData(prev => ({ ...prev, installationType: 'solar_battery' }))}
                icon={Battery}
                title="Solar + Battery"
                description="Solar panels with battery storage system"
              />
            </div>
            <Button
              onClick={goNext}
              disabled={!formData.installationType}
              className="w-full mt-6 bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 1:
        return (
          <div>
            <h2 className="text-xl font-bold text-[#3D5A47] mb-2 text-center">Phase Type</h2>
            <p className="text-gray-600 text-center mb-6">What type of electrical supply do you have?</p>
            <div className="space-y-3">
              <OptionCard
                selected={formData.phaseType === 'single'}
                onClick={() => setFormData(prev => ({ ...prev, phaseType: 'single' }))}
                icon={Zap}
                title="Single Phase"
                description="Most common for residential properties"
              />
              <OptionCard
                selected={formData.phaseType === 'three'}
                onClick={() => setFormData(prev => ({ ...prev, phaseType: 'three' }))}
                icon={Zap}
                title="Three Phase"
                description="Larger homes or commercial properties"
              />
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={goBack} variant="outline" className="flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={goNext}
                disabled={!formData.phaseType}
                className="flex-1 bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 2:
        // Backup options (only for solar+battery)
        if (formData.installationType === 'solar_battery') {
          return (
            <div>
              <h2 className="text-xl font-bold text-[#3D5A47] mb-2 text-center">Backup Options</h2>
              <p className="text-gray-600 text-center mb-6">What backup coverage do you need?</p>
              <div className="space-y-3">
                <OptionCard
                  selected={formData.backupType === 'none'}
                  onClick={() => setFormData(prev => ({ ...prev, backupType: 'none' }))}
                  icon={Battery}
                  title="No Backup"
                  description="Battery for self-consumption only, no backup during outages"
                />
                <OptionCard
                  selected={formData.backupType === 'essential'}
                  onClick={() => setFormData(prev => ({ ...prev, backupType: 'essential' }))}
                  icon={Shield}
                  title="Essential Backup"
                  description="Power key circuits during outages (lights, fridge, internet)"
                />
                <OptionCard
                  selected={formData.backupType === 'whole_home'}
                  onClick={() => setFormData(prev => ({ ...prev, backupType: 'whole_home' }))}
                  icon={Home}
                  title="Whole Home Backup"
                  description="Power your entire home during outages"
                />
              </div>
              
              {/* Smart Meter Note */}
              <div className="bg-blue-50 rounded-xl p-4 mt-4">
                <p className="text-sm text-blue-800">
                  <Info className="w-4 h-4 inline mr-2" />
                  <strong>Note:</strong> Battery installations require a smart meter. This will be factored into the space analysis.
                </p>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button onClick={goBack} variant="outline" className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button
                  onClick={goNext}
                  disabled={!formData.backupType}
                  className="flex-1 bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          );
        }
        // Fall through to image upload for solar only
        // eslint-disable-next-line no-fallthrough
      case 3:
        return (
          <div>
            <h2 className="text-xl font-bold text-[#3D5A47] mb-2 text-center">Upload Switchboard Photo</h2>
            <p className="text-gray-600 text-center mb-6">Take a clear photo of your main switchboard with the cover open</p>
            
            {/* Image upload area - improved for mobile */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#B5D43B] transition-colors active:bg-gray-50"
            >
              {previewImage === 'loading' ? (
                <div className="py-8">
                  <Loader2 className="w-12 h-12 text-[#B5D43B] mx-auto mb-3 animate-spin" />
                  <p className="text-gray-600 font-medium">Processing image...</p>
                </div>
              ) : previewImage ? (
                <div className="relative">
                  <img
                    src={previewImage}
                    alt="Switchboard preview"
                    className="max-h-64 mx-auto rounded-lg"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewImage(null);
                      setFormData(prev => ({ ...prev, imageBase64: '' }));
                      // Reset file input
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full shadow-lg"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                  <p className="text-green-600 text-sm mt-2 flex items-center justify-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Photo ready for analysis
                  </p>
                </div>
              ) : (
                <>
                  <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">Tap to take or select photo</p>
                  <p className="text-sm text-gray-400 mt-1">JPG, PNG, WEBP or HEIC (max 15MB)</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-xl p-4 mt-4">
              <p className="text-sm text-blue-800 font-medium mb-2">📸 Tips for a good photo:</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Open the switchboard cover/door</li>
                <li>• Ensure good lighting</li>
                <li>• Capture all circuit breakers clearly</li>
                <li>• Include empty spaces if any</li>
              </ul>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={goBack} variant="outline" className="flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={submitAnalysis}
                disabled={!formData.imageBase64 || isAnalyzing || previewImage === 'loading'}
                className="flex-1 bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    Analyze Switchboard <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-24 pb-16">
        {/* Hero */}
        <section className="bg-gradient-to-b from-[#3D5A47] to-[#2d4435] text-white py-12 mb-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">MSB Checker</h1>
            <p className="text-white/80">
              Check if your switchboard needs upgrading for solar installation
            </p>
          </div>
        </section>

        {/* Form */}
        <div className="max-w-lg mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
            <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
            {renderStep()}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MSBCheckerPage;
